import React from 'react';
import styled from 'styled-components';
import { FiArrowRight } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logoImage from '../assets/images/logo.jpg';

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #0A0A0A;
  color: white;
  overflow: hidden;
  position: relative;
`;

const AuthButtons = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 12px;
  z-index: 100;
`;

const AuthButton = styled(Link)`
  background-color: ${props => props.primary ? 'rgba(0, 0, 254, 0.9)' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    transform: translateY(-2px);
    background-color: ${props => props.primary ? 'rgba(0, 0, 254, 1)' : 'rgba(255, 255, 255, 0.15)'};
    box-shadow: 0 4px 10px rgba(0, 0, 254, 0.2);
  }
`;

const TopGlow = styled.div`
  position: absolute;
  top: -150px;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 300px;
  background: radial-gradient(circle, rgba(0, 0, 254, 0.4) 0%, rgba(0, 0, 0, 0) 70%);
  z-index: 1;
`;

const GridBackground = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(rgba(20, 20, 20, 0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(20, 20, 20, 0.05) 1px, transparent 1px);
  background-size: 50px 50px;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 0 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const LogoContainer = styled.div`
  position: relative;
  margin-bottom: 64px;
  padding-top: 80px;
`;

const LogoWrapper = styled.div`
  position: relative;
  display: inline-block;
  padding: 0.5px;
`;

const LogoInner = styled.div`
  overflow: hidden;
  padding: 12px;
`;

const Logo = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 16px;
  box-shadow: 0 0 30px rgba(0, 0, 254, 0.5);

  @media (min-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const ContentSection = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`;

const Heading = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  line-height: 1.2;
  margin-bottom: 2rem;
  letter-spacing: -0.03em;

  @media (min-width: 640px) {
    font-size: 3rem;
  }

  @media (min-width: 768px) {
    font-size: 3.5rem;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(to right, #0000fe, #6060ff);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  color: #aaaaaa;
  max-width: 600px;
  margin: 0 auto 3rem;
  line-height: 1.6;
  backdrop-filter: blur(5px);
  border-radius: 8px;
  padding: 10px;
`;

const CTAButton = styled(Link)`
  position: relative;
  display: inline-block;
  text-decoration: none;
`;

const ButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
  padding: 2px;
  border-radius: 10px;
  background: linear-gradient(90deg, #0000fe, #6060ff);
  overflow: hidden;
  z-index: 1;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 200%;
    height: 100%;
    background: linear-gradient(90deg, #6060ff, #0000fe, #6060ff);
    transition: all 0.6s ease;
    z-index: -1;
  }
  
  &:hover:before {
    left: 0;
  }
`;

const Button = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #0A0A0A;
  color: white;
  border-radius: 8px;
  padding: 12px 20px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  .highlight {
    color: #6060ff;
    margin-right: 6px;
  }
  
  svg {
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(3px);
  }
`;

function LandingPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  return (
    <PageContainer>
      <TopGlow />
      <GridBackground />
      
      {/* Auth buttons */}
      <AuthButtons>
        {isAuthenticated() ? (
          <AuthButton to="/chat" primary>Go to App</AuthButton>
        ) : (
          <>
            <AuthButton to="/login">Log in</AuthButton>
            <AuthButton to="/signup" primary>Sign up</AuthButton>
          </>
        )}
      </AuthButtons>
      
      <ContentWrapper>
        <LogoContainer>
          <LogoWrapper>
            <LogoInner>
              <Logo src={logoImage} alt="Blockchain AI Data Layer Logo" />
            </LogoInner>
          </LogoWrapper>
        </LogoContainer>
        
        <ContentSection>
          <h2>CA: ChRVUDE5BUmGjUPoNBXHn9GNsTWSNYiBB9ZdYzotbm1z</h2>
          <Heading>
            Instantly Connect to<br />
            <GradientText>Blockchain Data</GradientText><br />
            with Natural Language
          </Heading>
          
          <Subtitle>
          No dashboards. No queries. Just type what you want to know — your wallet, tokens, transactions, and more — and get real-time insights powered by AI.
          </Subtitle>
          
          <CTAButton to={isAuthenticated() ? "/chat" : "/signup"}>
            <ButtonWrapper>
              <Button>
                <span className="highlight">Try</span>
                <span>the assistant now</span>
                <FiArrowRight />
              </Button>
            </ButtonWrapper>
          </CTAButton>
        </ContentSection>
      </ContentWrapper>
    </PageContainer>
  );
}

export default LandingPage;
